import { useState } from "react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight, Handshake, Building2, GraduationCap, Newspaper, Globe2, CheckCircle2 } from "lucide-react";

const TYPES = [
  { id: "school", label: "School / University", icon: GraduationCap },
  { id: "ngo", label: "NGO / Non-profit", icon: Globe2 },
  { id: "corporate", label: "Corporate / Foundation", icon: Building2 },
  { id: "gov", label: "Government agency", icon: Handshake },
  { id: "media", label: "Media / Press", icon: Newspaper },
  { id: "other", label: "Other", icon: Handshake },
];

export default function Partner() {
  const [form, setForm] = useState({
    organization: "",
    contact_name: "",
    email: "",
    website: "",
    type: "ngo",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.organization || !form.contact_name || !form.email || !form.message) {
      toast.error("Please fill in organisation, name, email and message");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/partnerships", form);
      setSubmitted(true);
      toast.success("Thank you — we'll be in touch within a week.");
    } catch (e) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="partner-page" className="min-h-screen bg-[var(--bone)] text-[var(--ink)]">
      <Nav />

      <section className="pt-32 pb-16 md:pt-40">
        <div className="container-x">
          <div className="overline text-[var(--terracotta)]">Collaborate</div>
          <h1 className="font-serif text-5xl md:text-7xl mt-4 tracking-[-0.025em] leading-[0.95] max-w-5xl">
            Build with us. <em className="text-[var(--terracotta)] not-italic">We&apos;re better together.</em>
          </h1>
          <p className="text-[var(--ink)]/75 mt-8 max-w-2xl leading-relaxed text-lg">
            Schools, NGOs, corporates, government agencies, and media partners: we&apos;d love to
            hear from you. Tell us a little about your organisation and what you&apos;re hoping to
            build together — we read every message ourselves.
          </p>
        </div>
      </section>

      <section className="pb-24 md:pb-32">
        <div className="container-x grid grid-cols-12 gap-10 items-start">
          {/* Left: who we work with */}
          <aside className="col-span-12 md:col-span-4">
            <div className="overline text-[var(--terracotta)] mb-6">Who we work with</div>
            <ul className="space-y-5">
              {[
                { t: "Local schools", d: "Joint curriculum, teacher training, STEM lab co-design." },
                { t: "Peer NGOs", d: "Shared logistics in rural communities and joint funding applications." },
                { t: "Corporates & foundations", d: "Sponsor a build, fund a STEM cohort, embed employee volunteers." },
                { t: "Government partners", d: "Public-private classroom rebuilds and curriculum alignment." },
                { t: "Media & press", d: "Stories from the field, founder interviews, on-the-ground footage." },
              ].map((row) => (
                <li key={row.t} className="border-l-2 border-[var(--terracotta)] pl-4">
                  <div className="font-serif text-xl">{row.t}</div>
                  <div className="text-sm text-[var(--ink)]/70 mt-1">{row.d}</div>
                </li>
              ))}
            </ul>

            <div className="mt-10 bg-[var(--bone-2)] p-6 border border-black/10">
              <div className="overline text-[var(--terracotta)]">Direct contact</div>
              <p className="text-sm mt-3"><strong>Partnerships</strong><br/><a href="mailto:irenefoundation1@gmail.com" className="hover:text-[var(--terracotta)] break-all">irenefoundation1@gmail.com</a></p>
              <p className="text-sm mt-3"><strong>Press</strong><br/><a href="mailto:irenefoundation1@gmail.com" className="hover:text-[var(--terracotta)] break-all">irenefoundation1@gmail.com</a></p>
            </div>
          </aside>

          {/* Right: form */}
          <div className="col-span-12 md:col-span-8 md:pl-8">
            <div className="bg-[var(--forest)] text-[var(--bone)] p-10 md:p-14 relative overflow-hidden">
              <div className="grain absolute inset-0" />
              <div className="relative">
                {submitted ? (
                  <div data-testid="partner-thanks" className="py-6">
                    <CheckCircle2 className="w-10 h-10 text-[var(--terracotta)]" />
                    <h2 className="font-serif text-4xl mt-6 tracking-[-0.02em] leading-[1.05]">
                      Message received.
                    </h2>
                    <p className="text-[var(--bone)]/75 mt-4 max-w-md">
                      Someone from our partnerships team will reach out personally within
                      one week. Thank you for thinking of Irene Foundation.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={submit} data-testid="partner-form">
                    <div className="overline text-[var(--terracotta)] inline-flex items-center gap-2">
                      <Handshake className="w-3.5 h-3.5" /> Start a conversation
                    </div>
                    <h2 className="font-serif text-3xl md:text-4xl mt-4 tracking-[-0.02em] leading-[1.05]">
                      Tell us about your organisation.
                    </h2>

                    {/* Type chooser */}
                    <div className="mt-8">
                      <div className="overline text-[var(--bone)]/60 mb-3">What are you?</div>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                        {TYPES.map(({ id, label, icon: Icon }) => (
                          <button
                            key={id}
                            type="button"
                            data-testid={`partner-type-${id}`}
                            onClick={() => upd("type", id)}
                            className={`text-left flex items-center gap-2 px-3 py-3 border transition-colors text-xs ${
                              form.type === id
                                ? "bg-[var(--terracotta)] border-[var(--terracotta)] text-[var(--bone)]"
                                : "border-[var(--bone)]/25 hover:border-[var(--bone)]/70 text-[var(--bone)]/90"
                            }`}
                          >
                            <Icon className="w-3.5 h-3.5 shrink-0" /> {label}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
                      <label className="block">
                        <span className="overline text-[var(--bone)]/60">Organisation</span>
                        <input data-testid="partner-org" required className="editorial-input on-dark mt-2" value={form.organization} onChange={(e) => upd("organization", e.target.value)} placeholder="Acme Foundation" />
                      </label>
                      <label className="block">
                        <span className="overline text-[var(--bone)]/60">Contact name</span>
                        <input data-testid="partner-name" required className="editorial-input on-dark mt-2" value={form.contact_name} onChange={(e) => upd("contact_name", e.target.value)} placeholder="Your full name" />
                      </label>
                      <label className="block">
                        <span className="overline text-[var(--bone)]/60">Work email</span>
                        <input data-testid="partner-email" type="email" required className="editorial-input on-dark mt-2" value={form.email} onChange={(e) => upd("email", e.target.value)} placeholder="you@org.com" />
                      </label>
                      <label className="block">
                        <span className="overline text-[var(--bone)]/60">Website (optional)</span>
                        <input data-testid="partner-website" className="editorial-input on-dark mt-2" value={form.website} onChange={(e) => upd("website", e.target.value)} placeholder="https://…" />
                      </label>
                      <label className="block md:col-span-2">
                        <span className="overline text-[var(--bone)]/60">What would you like to build with us?</span>
                        <textarea data-testid="partner-message" required rows={4} className="editorial-input on-dark mt-2 resize-none" value={form.message} onChange={(e) => upd("message", e.target.value)} placeholder="A short intro — your mission, the geography, the idea." />
                      </label>
                    </div>
                    <button data-testid="partner-submit" type="submit" disabled={submitting} className="btn-primary mt-10 disabled:opacity-60">
                      {submitting ? "Sending…" : "Send to the partnerships team"} <ArrowRight className="w-4 h-4" />
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
